// UserActivityLogs.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Container, FilterBar, FilterGroup, FilterLabel, FilterInput,
    FilterSelect, DateInput, FilterActions, SearchButton, ResetButton,
    SummaryBar, TableWrapper, Table, TableHead, TableHeader,
    TableBody, TableRow, TableData, EventBadge, EntityChip,
    DetailBox, DetailLine, DetailTooltip, TooltipWrapper, AvatarCircle, UserCell, UserName,
    UserId, TimeCell, StateBox, StateIcon, StateText, StateSubText,
    PaginationContainer, RowsSelect, PageSelect, PageButton,
    ErrorBanner, SpinnerRing,
} from '../style-comps/UserActivityLogs.js.styles.js';
import { getAuditLogs, getAuditLogEventTypes, getAuditLogEntityTypes } from '../services/api';

// ─── Constants ────────────────────────────────────────────────────────────────
const DEFAULT_PER_PAGE = 50;

// 5 seconds: used to merge role removed + role assigned into one visual row
const ROLE_GROUP_WINDOW_MS = 5000;

// Front-end fallback only.
// Long-term best practice: backend should include roleName for both assigned and removed role events.
const ROLE_NAME_MAP = {
    4: 'Owner',
    5: 'OwnerWithEditPrices',
};

const PERMISSION_NAME_MAP = {
    'Listings.ViewPrices': 'View listing prices',
    'Listings.EditPrices': 'Edit listing prices',
};

const EVENT_TYPE_LABEL_MAP = {
    ListingPriceUpdated: 'Listing price updated',
    RoleCreated: 'Role created',
    RoleDeleted: 'Role deleted',
    RolePermissionRemoved: 'Role permission removed',
    RolePermissionSet: 'Role permission set',
    RoleUpdated: 'Role updated',
    UserListingAssigned: 'User listing assigned',
    UserListingRemoved: 'User listing removed',
    UserPasswordChanged: 'User password changed',
    UserPermissionRemoved: 'User permission removed',
    UserPermissionSet: 'User permission set',
    UserRoleAssigned: 'User role assigned',
    UserRoleRemoved: 'User role removed',
    UserRoleChanged: 'User role changed',
};

const ENTITY_TYPE_LABEL_MAP = {
    Listing: 'Listing',
    ListingPrice: 'Listing price',
    Role: 'Role',
    RolePermission: 'Role permission',
    User: 'User',
    UserListing: 'User listing',
    UserPermission: 'User permission',
    UserRole: 'User role',
};

const splitPascalCase = (value = '') =>
    String(value)
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .trim();

const formatEventTypeLabel = (eventType = '') =>
    EVENT_TYPE_LABEL_MAP[eventType] || splitPascalCase(eventType);

const formatEntityTypeLabel = (entityType = '') =>
    ENTITY_TYPE_LABEL_MAP[entityType] || splitPascalCase(entityType);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatUtcDisplay = (iso) => {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, '0');
        // Short format: MM/DD HH:mm
        return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
        return iso;
    }
};

const formatUtcFull = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-AU', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });
    } catch {
        return iso;
    }
};

const toLocalDatetimeValue = (iso) => {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
        return '';
    }
};

const localToUtcIso = (localVal) => {
    if (!localVal) return '';
    try {
        return new Date(localVal).toISOString();
    } catch {
        return '';
    }
};

const getInitials = (name = '') =>
    String(name)
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join('');

const parseMetadataJson = (value) => {
    if (!value || typeof value !== 'string') return null;
    try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
        return null;
    }
};

const getLogMetadata = (log = {}) => {
    const meta = log.Metadata || log.metadata;
    if (meta && typeof meta === 'object') return meta;

    return parseMetadataJson(log.MetadataJson || log.metadataJson);
};

const getVal = (metadata = {}, ...keys) => {
    for (const key of keys) {
        const value = metadata?.[key];
        if (value !== undefined && value !== null && value !== '') return value;
    }
    return null;
};

const resolveRoleName = (roleId, roleName) => {
    if (roleName) return roleName;

    const mapped = ROLE_NAME_MAP[Number(roleId)];
    return mapped || null;
};

const normalizePermissionName = (permissionName) => {
    if (!permissionName) return null;
    return PERMISSION_NAME_MAP[permissionName] || permissionName;
};

const scopeLabel = (scope) => {
    const normalized = Number(scope);

    if (normalized === 2) return 'All access';
    if (normalized === 1) return 'Own access only';
    if (normalized === 0) return 'No access';

    return String(scope);
};

const getRoleDetail = (metadata = {}) => {
    const roleId = getVal(metadata, 'roleId', 'RoleId');
    const roleName = getVal(metadata, 'roleName', 'RoleName');
    const role = resolveRoleName(roleId, roleName);

    return { roleId, role };
};

const formatCurrencyValue = (value, currency = '') => {
    if (value === undefined || value === null || value === '') return null;

    const num = Number(value);
    const readableValue = Number.isFinite(num) ? num.toLocaleString('en-AU') : String(value);

    return currency ? `${currency} ${readableValue}` : readableValue;
};

const formatDateValue = (value) => {
    if (!value) return null;

    try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return String(value);

        return date.toLocaleDateString('en-AU', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
        });
    } catch {
        return String(value);
    }
};

const getListingDetail = (metadata = {}) => {
    const listingId = getVal(metadata, 'listingId', 'ListingId', 'propertyId', 'PropertyId');
    const listingName = getVal(
        metadata,
        'listingName',
        'ListingName',
        'listingTitle',
        'ListingTitle',
        'propertyName',
        'PropertyName',
        'title',
        'Title',
        'name',
        'Name'
    );

    return { listingId, listingName };
};

const getDateRangeDetail = (metadata = {}) => {
    const start = getVal(metadata, 'startDate', 'StartDate', 'fromDate', 'FromDate', 'dateFrom', 'DateFrom');
    const end = getVal(metadata, 'endDate', 'EndDate', 'toDate', 'ToDate', 'dateTo', 'DateTo');
    const single = getVal(metadata, 'date', 'Date', 'priceDate', 'PriceDate');

    if (start && end) return `${formatDateValue(start)} → ${formatDateValue(end)}`;
    if (single) return formatDateValue(single);

    return null;
};

const getChangedFields = (metadata = {}) => {
    const fields = getVal(metadata, 'updatedFields', 'UpdatedFields', 'changedFields', 'ChangedFields', 'fields', 'Fields');

    if (!fields) return null;
    return Array.isArray(fields) ? fields.join(', ') : String(fields);
};

// ─── Metadata detail builder ──────────────────────────────────────────────────
// Returns { lines: string[], accent: string } or null
const parseMetadataDetail = (eventType = '', metadata = null) => {
    if (!metadata || typeof metadata !== 'object') return null;

    const et = eventType.toLowerCase();

    // ── Password ──────────────────────────────────────────────────────────────
    if (et.includes('password')) {
        const src = getVal(metadata, 'source', 'Source');
        const reqChange = metadata.requirePasswordChange ?? metadata.RequirePasswordChange;

        const lines = [];

        if (src === 'AdminReset') {
            lines.push('Admin reset the user password.');
        } else if (src === 'UserPatch') {
            lines.push('User changed their password.');
        } else {
            lines.push('Password was changed.');
        }

        if (reqChange === true) {
            lines.push('User must change their password on next login.');
        }

        if (reqChange === false) {
            lines.push('Password change is completed.');
        }

        return { lines, accent: '#EFF6FF' };
    }

    // ── Role ──────────────────────────────────────────────────────────────────
    const { roleId, role } = getRoleDetail(metadata);

    const oldRoleId = getVal(metadata, 'oldRoleId', 'OldRoleId');
    const oldRoleName = getVal(metadata, 'oldRoleName', 'OldRoleName');
    const newRoleId = getVal(metadata, 'newRoleId', 'NewRoleId');
    const newRoleName = getVal(metadata, 'newRoleName', 'NewRoleName');

    const oldRole = resolveRoleName(oldRoleId, oldRoleName);
    const newRole = resolveRoleName(newRoleId, newRoleName);

    if (et === 'rolecreated') {
        const lines = [
            role
                ? `Created ${role} role.`
                : roleId
                    ? `Created role ID ${roleId}.`
                    : 'Created a role.',
        ];

        if (role && roleId) lines.push(`Role ID: ${roleId}`);

        return { lines, accent: '#F0FDF4' };
    }

    if (et === 'roledeleted') {
        const lines = [
            role
                ? `Deleted ${role} role.`
                : roleId
                    ? `Deleted role ID ${roleId}.`
                    : 'Deleted a role.',
        ];

        if (role && roleId) lines.push(`Role ID: ${roleId}`);

        return { lines, accent: '#FEF2F2' };
    }

    if (et === 'roleupdated' || et.includes('rolechanged')) {
        const fields = getChangedFields(metadata);

        const lines = [
            oldRole && newRole
                ? `Changed role from ${oldRole} to ${newRole}.`
                : role
                    ? `Updated ${role} role.`
                    : 'Updated user role.',
        ];

        if (fields) lines.push(`Fields: ${fields}`);
        if (oldRoleId || newRoleId) lines.push(`Role ID: ${oldRoleId || roleId || '—'} → ${newRoleId || roleId || '—'}`);
        else if (roleId) lines.push(`Role ID: ${roleId}`);

        return { lines, accent: '#FFFBEB' };
    }

    if (
        et.includes('roleassigned') ||
        et.includes('roleadded') ||
        et.includes('rolegranted')
    ) {
        const lines = [
            role
                ? `Assigned ${role} role.`
                : roleId
                    ? `Assigned role ID ${roleId}.`
                    : 'Assigned a role.',
        ];

        if (role && roleId) lines.push(`Role ID: ${roleId}`);

        return { lines, accent: '#F0FDF4' };
    }

    if (
        et.includes('roleremoved') ||
        et.includes('rolerevoked')
    ) {
        const lines = [
            role
                ? `Removed ${role} role.`
                : roleId
                    ? `Removed role ID ${roleId}.`
                    : 'Removed a role.',
        ];

        if (role && roleId) lines.push(`Role ID: ${roleId}`);

        return { lines, accent: '#FEF2F2' };
    }

    // ── Permission ────────────────────────────────────────────────────────────
    if (et.includes('permission')) {
        const permissionName = getVal(metadata, 'permissionName', 'PermissionName', 'permission');
        const permission = normalizePermissionName(permissionName);

        const scope = metadata.scope ?? metadata.Scope;
        const oldScope = metadata.oldScope ?? metadata.OldScope;
        const newScope = metadata.newScope ?? metadata.NewScope;

        const isRolePermission = et.includes('rolepermission');
        const isUserPermission = et.includes('userpermission');
        const isRemove = et.includes('remove') || et.includes('revoke') || et.includes('delete');
        const isSet = et.includes('set') || et.includes('add') || et.includes('grant');
        const isUpdate = et.includes('update') || et.includes('change') || et.includes('scope');

        const subject = isRolePermission
            ? role
                ? `${role} role`
                : roleId
                    ? `role ID ${roleId}`
                    : 'role'
            : isUserPermission
                ? 'user'
                : 'permission target';

        const lines = [];

        if (isRemove) {
            lines.push(
                permission
                    ? `Removed ${permission} from ${subject}.`
                    : `Removed a permission from ${subject}.`
            );
        } else if ((isSet || isUpdate) && permission && oldScope !== undefined && newScope !== undefined) {
            lines.push(`Changed ${permission} for ${subject} from ${scopeLabel(oldScope)} to ${scopeLabel(newScope)}.`);
        } else if ((isSet || isUpdate) && permission && scope !== undefined && scope !== null) {
            lines.push(`Set ${permission} for ${subject} to ${scopeLabel(scope)}.`);
        } else if (isSet && permission) {
            lines.push(`Granted ${permission} to ${subject}.`);
        } else {
            lines.push(permission ? `Updated ${permission} for ${subject}.` : `Updated permissions for ${subject}.`);
        }

        if (permissionName && permissionName !== permission) lines.push(`Permission: ${permissionName}`);
        if (role && roleId && isRolePermission) lines.push(`Role ID: ${roleId}`);
        if (scope !== undefined && scope !== null) lines.push(`Scope: ${scopeLabel(scope)}`);
        if (oldScope !== undefined && newScope !== undefined) lines.push(`Scope: ${scopeLabel(oldScope)} → ${scopeLabel(newScope)}`);

        return { lines, accent: isRemove ? '#FEF2F2' : '#FFF7ED' };
    }

    // ── User listing access ───────────────────────────────────────────────────
    if (et.includes('userlisting')) {
        const { listingId, listingName } = getListingDetail(metadata);

        const listingText = listingName
            ? `${listingName}${listingId ? ` (ID ${listingId})` : ''}`
            : listingId
                ? `listing ID ${listingId}`
                : 'a listing';

        const isRemove = et.includes('removed') || et.includes('delete') || et.includes('revoke');

        const lines = [
            isRemove
                ? `Removed user access to ${listingText}.`
                : `Assigned user access to ${listingText}.`,
        ];

        return { lines, accent: isRemove ? '#FEF2F2' : '#F0FDF4' };
    }

    // ── Listing price ─────────────────────────────────────────────────────────
    if (et.includes('listingprice') || (et.includes('listing') && et.includes('price'))) {
        const { listingId, listingName } = getListingDetail(metadata);
        const currency = getVal(metadata, 'currency', 'Currency') || '';

        const oldPrice = getVal(metadata, 'oldPrice', 'OldPrice', 'previousPrice', 'PreviousPrice', 'fromPrice', 'FromPrice');
        const newPrice = getVal(metadata, 'newPrice', 'NewPrice', 'price', 'Price', 'toPrice', 'ToPrice');
        const dateRange = getDateRangeDetail(metadata);

        const formattedOld = formatCurrencyValue(oldPrice, currency);
        const formattedNew = formatCurrencyValue(newPrice, currency);

        const lines = [];

        if (formattedOld && formattedNew) {
            lines.push(`Updated listing price from ${formattedOld} to ${formattedNew}.`);
        } else if (formattedNew) {
            lines.push(`Set listing price to ${formattedNew}.`);
        } else {
            lines.push('Updated listing price.');
        }

        if (listingName) lines.push(`Listing: ${listingName}${listingId ? ` (ID ${listingId})` : ''}`);
        else if (listingId) lines.push(`Listing ID: ${listingId}`);

        if (dateRange) lines.push(`Date: ${dateRange}`);

        return { lines, accent: '#EFF6FF' };
    }

    // ── User CRUD ─────────────────────────────────────────────────────────────
    if (et.includes('usercreated')) {
        const email = getVal(metadata, 'email', 'Email');
        const name = getVal(metadata, 'userName', 'UserName', 'name', 'Name');

        const lines = [
            name || email
                ? `Created user account for ${name || email}.`
                : 'Created a user account.',
        ];

        if (name && email) lines.push(`Email: ${email}`);

        return { lines, accent: '#F0FDF4' };
    }

    if (et.includes('userdeleted') || et.includes('userremoved')) {
        const email = getVal(metadata, 'email', 'Email');
        const name = getVal(metadata, 'userName', 'UserName', 'name', 'Name');

        const lines = [
            name || email
                ? `Deleted user account for ${name || email}.`
                : 'Deleted a user account.',
        ];

        return { lines, accent: '#FEF2F2' };
    }

    if (et.includes('userupdated') || et.includes('userpatch') || et.includes('userchanged')) {
        const fields = getChangedFields(metadata);

        const lines = ['Updated user profile details.'];

        if (fields) lines.push(`Fields: ${fields}`);

        return { lines, accent: '#FFFBEB' };
    }

    // ── Login / Auth ──────────────────────────────────────────────────────────
    if (et.includes('login')) {
        const ip = getVal(metadata, 'ip', 'Ip', 'ipAddress', 'IpAddress');
        const success = metadata.success ?? metadata.Success;
        const reason = getVal(metadata, 'failReason', 'FailReason', 'reason', 'Reason');

        const lines = [success === false ? 'Login failed.' : 'Login succeeded.'];

        if (ip) lines.push(`IP: ${ip}`);
        if (reason) lines.push(`Reason: ${reason}`);

        return { lines, accent: success === false ? '#FEF2F2' : '#F0FDF4' };
    }

    if (et.includes('logout')) {
        return { lines: ['User logged out.'], accent: '#F9FAFB' };
    }

    // ── Listing / Property ────────────────────────────────────────────────────
    if (et.includes('listing')) {
        const { listingId, listingName } = getListingDetail(metadata);

        const isAdd = et.includes('creat') || et.includes('add');
        const isDel = et.includes('delet') || et.includes('remov');

        const lines = [
            isAdd
                ? 'Created a listing.'
                : isDel
                    ? 'Deleted a listing.'
                    : 'Updated listing details.',
        ];

        if (listingName) lines.push(`Listing: ${listingName}${listingId ? ` (ID ${listingId})` : ''}`);
        else if (listingId) lines.push(`Listing ID: ${listingId}`);

        return { lines, accent: isDel ? '#FEF2F2' : '#EFF6FF' };
    }

    // ── Generic fallback ──────────────────────────────────────────────────────
    const SKIP = new Set(['id', 'Id', 'userId', 'UserId', 'actorUserId', 'targetUserId']);

    const LABEL_MAP = {
        source: 'Source',
        Source: 'Source',
        roleName: 'Role',
        RoleName: 'Role',
        roleId: 'Role ID',
        RoleId: 'Role ID',
        permissionName: 'Permission',
        PermissionName: 'Permission',
        scope: 'Scope',
        Scope: 'Scope',
        email: 'Email',
        Email: 'Email',
        requirePasswordChange: 'Require password change',
        RequirePasswordChange: 'Require password change',
    };

    const entries = Object.entries(metadata)
        .filter(([key, value]) => !SKIP.has(key) && value !== null && value !== undefined && value !== '')
        .slice(0, 4)
        .map(([key, value]) => {
            const label = LABEL_MAP[key] || splitPascalCase(key);
            const val = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value);
            return `${label}: ${val}`;
        });

    return entries.length ? { lines: entries, accent: '#F9FAFB' } : null;
};

// ─── Group role change rows ───────────────────────────────────────────────────
const isRoleAssignedLog = (log = {}) => {
    const eventType = String(log.EventType || log.eventType || '').toLowerCase();

    return (
        eventType.includes('roleassigned') ||
        eventType.includes('roleadded') ||
        eventType.includes('rolegranted')
    );
};

const isRoleRemovedLog = (log = {}) => {
    const eventType = String(log.EventType || log.eventType || '').toLowerCase();

    return (
        eventType.includes('roleremoved') ||
        eventType.includes('rolerevoked') ||
        eventType.includes('roledeleted')
    );
};

const getLogTime = (log = {}) => {
    const raw = log.CreatedAtUtc || log.timestamp || log.createdAt;
    const time = raw ? new Date(raw).getTime() : NaN;

    return Number.isFinite(time) ? time : null;
};

const sameRoleChangeContext = (a = {}, b = {}) => {
    const sameActor =
        String(a.ActorUserId || a.actorUserId || '') ===
        String(b.ActorUserId || b.actorUserId || '');

    const sameTarget =
        String(a.TargetUserId || a.targetUserId || '') ===
        String(b.TargetUserId || b.targetUserId || '');

    const sameEntity =
        String(a.EntityType || a.entityType || '') ===
        String(b.EntityType || b.entityType || '');

    const at = getLogTime(a);
    const bt = getLogTime(b);

    const closeEnough =
        at !== null &&
        bt !== null &&
        Math.abs(at - bt) <= ROLE_GROUP_WINDOW_MS;

    return sameActor && sameTarget && sameEntity && closeEnough;
};

const buildRoleChangeLog = (removedLog, assignedLog) => {
    const removedMeta = getLogMetadata(removedLog) || {};
    const assignedMeta = getLogMetadata(assignedLog) || {};

    const oldRoleId = getVal(removedMeta, 'roleId', 'RoleId');
    const oldRoleName = resolveRoleName(oldRoleId, getVal(removedMeta, 'roleName', 'RoleName'));

    const newRoleId = getVal(assignedMeta, 'roleId', 'RoleId');
    const newRoleName = resolveRoleName(newRoleId, getVal(assignedMeta, 'roleName', 'RoleName'));

    const latestLog =
        (getLogTime(removedLog) || 0) >= (getLogTime(assignedLog) || 0)
            ? removedLog
            : assignedLog;

    const olderLog = latestLog === removedLog ? assignedLog : removedLog;

    return {
        ...latestLog,
        Id: `role-change-${removedLog.Id ?? removedLog.id}-${assignedLog.Id ?? assignedLog.id}`,
        EventType: 'UserRoleChanged',
        eventType: 'UserRoleChanged',
        EntityType: latestLog.EntityType || latestLog.entityType || 'UserRole',
        entityType: latestLog.EntityType || latestLog.entityType || 'UserRole',
        EntityId: latestLog.TargetUserId || latestLog.targetUserId || latestLog.EntityId || latestLog.entityId,
        entityId: latestLog.TargetUserId || latestLog.targetUserId || latestLog.EntityId || latestLog.entityId,
        Metadata: {
            source: 'GroupedRoleChange',
            oldRoleId,
            oldRoleName,
            newRoleId,
            newRoleName,
            removedLogId: removedLog.Id ?? removedLog.id,
            assignedLogId: assignedLog.Id ?? assignedLog.id,
        },
        MetadataJson: null,
        _groupedLogIds: [olderLog.Id ?? olderLog.id, latestLog.Id ?? latestLog.id].filter(Boolean),
    };
};

const groupAuditLogs = (items = []) => {
    const grouped = [];
    const used = new Set();

    for (let i = 0; i < items.length; i += 1) {
        if (used.has(i)) continue;

        const current = items[i];
        let matchedIndex = -1;

        // Only look at nearby rows, because the backend returns related actions close together.
        for (let j = i + 1; j < Math.min(items.length, i + 4); j += 1) {
            if (used.has(j)) continue;

            const candidate = items[j];

            const isPair =
                (
                    (isRoleRemovedLog(current) && isRoleAssignedLog(candidate)) ||
                    (isRoleAssignedLog(current) && isRoleRemovedLog(candidate))
                ) &&
                sameRoleChangeContext(current, candidate);

            if (isPair) {
                matchedIndex = j;
                break;
            }
        }

        if (matchedIndex >= 0) {
            const next = items[matchedIndex];

            const removedLog = isRoleRemovedLog(current) ? current : next;
            const assignedLog = isRoleAssignedLog(current) ? current : next;

            grouped.push(buildRoleChangeLog(removedLog, assignedLog));

            used.add(i);
            used.add(matchedIndex);
        } else {
            grouped.push(current);
            used.add(i);
        }
    }

    return grouped;
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const Spinner = () => <SpinnerRing />;

const Avatar = ({ name }) => (
    <AvatarCircle>{getInitials(name) || '?'}</AvatarCircle>
);

const LogRow = ({ log }) => {
    const eventType = log.EventType || log.eventType || '';
    const meta = getLogMetadata(log);
    const detail = parseMetadataDetail(eventType, meta);

    return (
        <TableRow>
            {/* Time */}
            <TableData>
                <TimeCell title={formatUtcFull(log.CreatedAtUtc || log.timestamp || log.createdAt)}>
                    {formatUtcDisplay(log.CreatedAtUtc || log.timestamp || log.createdAt)}
                </TimeCell>
            </TableData>

            {/* Actor */}
            <TableData>
                <UserCell>
                    <Avatar name={log.ActorUserName || log.actorUserName || log.ActorUserId} />
                    <div>
                        <UserName>{log.ActorUserName || log.actorUserName || '—'}</UserName>
                        {(log.ActorUserId || log.actorUserId) && (
                            <UserId>ID: {log.ActorUserId || log.actorUserId}</UserId>
                        )}
                    </div>
                </UserCell>
            </TableData>

            {/* Target user */}
            <TableData>
                {(log.TargetUserName || log.targetUserName || log.TargetUserId || log.targetUserId) ? (
                    <UserCell>
                        <Avatar name={log.TargetUserName || log.targetUserName || log.TargetUserId} />
                        <div>
                            <UserName>{log.TargetUserName || log.targetUserName || '—'}</UserName>
                            {(log.TargetUserId || log.targetUserId) && (
                                <UserId>ID: {log.TargetUserId || log.targetUserId}</UserId>
                            )}
                        </div>
                    </UserCell>
                ) : (
                    <span style={{ color: '#9CA3AF' }}>—</span>
                )}
            </TableData>

            {/* Event type */}
            <TableData>
                <EventBadge type={eventType}>{formatEventTypeLabel(eventType) || '—'}</EventBadge>
            </TableData>

            {/* Entity type */}
            <TableData>
                {(log.EntityType || log.entityType)
                    ? <EntityChip>{formatEntityTypeLabel(log.EntityType || log.entityType)}</EntityChip>
                    : <span style={{ color: '#9CA3AF' }}>—</span>
                }
            </TableData>

            {/* Details */}
            <TableData>
                {detail ? (
                    <TooltipWrapper>
                        <DetailBox accent={detail.accent}>
                            {detail.lines[0]}
                        </DetailBox>
                        <DetailTooltip className="detail-tooltip" accent={detail.accent}>
                            {detail.lines.map((line, i) => (
                                <DetailLine key={i} first={i === 0}>{line}</DetailLine>
                            ))}
                        </DetailTooltip>
                    </TooltipWrapper>
                ) : (
                    <span style={{ color: '#9CA3AF' }}>—</span>
                )}
            </TableData>
        </TableRow>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────
const UserActivityLogs = () => {
    const [filters, setFilters] = useState({
        actorUserId: '',
        targetUserId: '',
        eventType: '',
        entityType: '',
        entityId: '',
        fromUtc: '',
        toUtc: '',
    });

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
    const [logs, setLogs] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [eventTypes, setEventTypes] = useState([]);
    const [entityTypes, setEntityTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);

    const abortRef = useRef(null);

    // ── Dropdowns ─────────────────────────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;

        const fetchDropdowns = async (attempt = 1) => {
            try {
                const [evData, enData] = await Promise.all([
                    getAuditLogEventTypes(),
                    getAuditLogEntityTypes(),
                ]);

                if (cancelled) return;

                const toArray = (d) =>
                    Array.isArray(d)
                        ? d
                        : Array.isArray(d?.Data)
                            ? d.Data
                            : Array.isArray(d?.data)
                                ? d.data
                                : [];

                setEventTypes(toArray(evData));
                setEntityTypes(toArray(enData));
            } catch {
                if (!cancelled && attempt < 3) {
                    setTimeout(() => fetchDropdowns(attempt + 1), attempt * 800);
                }
            }
        };

        fetchDropdowns();

        return () => {
            cancelled = true;
        };
    }, []);

    // ── Fetch logs ────────────────────────────────────────────────────────────
    const fetchLogs = useCallback(async (currentPage = 1, currentPerPage = perPage) => {
        if (abortRef.current) abortRef.current.abort();

        const ctrl = new AbortController();
        abortRef.current = ctrl;

        setLoading(true);
        setError(null);

        const params = {
            ...filters,
            fromUtc: localToUtcIso(filters.fromUtc),
            toUtc: localToUtcIso(filters.toUtc),
            page: currentPage,
            perPage: currentPerPage,
        };

        try {
            const json = await getAuditLogs(params);

            const items =
                json.Items ||
                json.data ||
                json.items ||
                json.logs ||
                json.results ||
                [];

            const total =
                json.TotalCount ??
                json.totalCount ??
                json.total ??
                json.pagination?.total ??
                items.length;

            setLogs(items);
            setTotalCount(total);
            setHasSearched(true);
        } catch (err) {
            if (err.name !== 'AbortError') {
                setError(err.message || 'Search failed. Please try again later.');
            }
        } finally {
            setLoading(false);
        }
    }, [filters, perPage]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleFilterChange = (key) => (e) =>
        setFilters((prev) => ({ ...prev, [key]: e.target.value }));

    const handleSearch = () => {
        setPage(1);
        fetchLogs(1, perPage);
    };

    const handleReset = () => {
        setFilters({
            actorUserId: '',
            targetUserId: '',
            eventType: '',
            entityType: '',
            entityId: '',
            fromUtc: '',
            toUtc: '',
        });

        setPage(1);
        setLogs([]);
        setTotalCount(0);
        setHasSearched(false);
        setError(null);
    };

    const totalPages = Math.max(1, Math.ceil(totalCount / perPage));

    const handlePageChange = (newPage) => {
        const p = Math.max(1, Math.min(newPage, totalPages));

        setPage(p);
        fetchLogs(p, perPage);
    };

    const handlePerPageChange = (e) => {
        const val = Number(e.target.value);

        setPerPage(val);
        setPage(1);
        fetchLogs(1, val);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSearch();
    };

    const displayLogs = groupAuditLogs(logs);

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Container>
            {/* Filter Bar */}
            <FilterBar>
                <FilterGroup>
                    <FilterLabel>Actor User ID</FilterLabel>
                    <FilterInput
                        type="number"
                        placeholder="Actor user ID"
                        value={filters.actorUserId}
                        onChange={handleFilterChange('actorUserId')}
                        onKeyDown={handleKeyDown}
                    />
                </FilterGroup>

                <FilterGroup>
                    <FilterLabel>Target User ID</FilterLabel>
                    <FilterInput
                        type="number"
                        placeholder="Target user ID"
                        value={filters.targetUserId}
                        onChange={handleFilterChange('targetUserId')}
                        onKeyDown={handleKeyDown}
                    />
                </FilterGroup>

                <FilterGroup>
                    <FilterLabel>Event Type</FilterLabel>
                    <FilterSelect value={filters.eventType} onChange={handleFilterChange('eventType')}>
                        <option value="">All event types</option>
                        {eventTypes.map((t) => (
                            <option key={t} value={t}>
                                {formatEventTypeLabel(t)}
                            </option>
                        ))}
                    </FilterSelect>
                </FilterGroup>

                <FilterGroup>
                    <FilterLabel>Entity Type</FilterLabel>
                    <FilterSelect value={filters.entityType} onChange={handleFilterChange('entityType')}>
                        <option value="">All entity types</option>
                        {entityTypes.map((t) => (
                            <option key={t} value={t}>
                                {formatEntityTypeLabel(t)}
                            </option>
                        ))}
                    </FilterSelect>
                </FilterGroup>

                <FilterGroup>
                    <FilterLabel>Entity ID</FilterLabel>
                    <FilterInput
                        type="text"
                        placeholder="Entity ID"
                        value={filters.entityId}
                        onChange={handleFilterChange('entityId')}
                        onKeyDown={handleKeyDown}
                    />
                </FilterGroup>

                <FilterGroup>
                    <FilterLabel>Start Time</FilterLabel>
                    <DateInput
                        type="datetime-local"
                        value={toLocalDatetimeValue(filters.fromUtc)}
                        onChange={(e) =>
                            setFilters((prev) => ({ ...prev, fromUtc: e.target.value }))
                        }
                    />
                </FilterGroup>

                <FilterGroup>
                    <FilterLabel>End Time</FilterLabel>
                    <DateInput
                        type="datetime-local"
                        value={toLocalDatetimeValue(filters.toUtc)}
                        onChange={(e) =>
                            setFilters((prev) => ({ ...prev, toUtc: e.target.value }))
                        }
                    />
                </FilterGroup>

                <FilterActions>
                    <ResetButton onClick={handleReset}>Reset</ResetButton>
                    <SearchButton onClick={handleSearch} disabled={loading}>
                        {loading ? 'Searching…' : '🔍 Search'}
                    </SearchButton>
                </FilterActions>
            </FilterBar>

            {/* Error */}
            {error && <ErrorBanner>⚠️ {error}</ErrorBanner>}

            {/* Summary */}
            {hasSearched && !loading && (
                <SummaryBar>
                    <span>
                        <strong>{totalCount.toLocaleString()}</strong> results
                        {totalPages > 1 && (
                            <>
                                {', page '}
                                <strong>{page}</strong>
                                {` of ${totalPages}`}
                            </>
                        )}
                    </span>
                </SummaryBar>
            )}

            {/* Table */}
            <TableWrapper>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableHeader style={{ width: '13%' }}>Time</TableHeader>
                            <TableHeader style={{ width: '16%' }}>Actor</TableHeader>
                            <TableHeader style={{ width: '16%' }}>Target User</TableHeader>
                            <TableHeader style={{ width: '14%' }}>Event Type</TableHeader>
                            <TableHeader style={{ width: '10%' }}>Entity Type</TableHeader>
                            <TableHeader>Details</TableHeader>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loading ? (
                            <tr>
                                <td colSpan={6}>
                                    <StateBox>
                                        <Spinner />
                                        <StateText>Loading…</StateText>
                                    </StateBox>
                                </td>
                            </tr>
                        ) : !hasSearched ? (
                            <tr>
                                <td colSpan={6}>
                                    <StateBox>
                                        <StateIcon>🔍</StateIcon>
                                        <StateText>Set filters and click Search</StateText>
                                        <StateSubText>The default search loads audit logs from the last 30 days</StateSubText>
                                    </StateBox>
                                </td>
                            </tr>
                        ) : displayLogs.length === 0 ? (
                            <tr>
                                <td colSpan={6}>
                                    <StateBox>
                                        <StateIcon>📭</StateIcon>
                                        <StateText>No matching records found</StateText>
                                        <StateSubText>Adjust the filters and try again</StateSubText>
                                    </StateBox>
                                </td>
                            </tr>
                        ) : (
                            displayLogs.map((log, idx) => (
                                <LogRow key={log.Id ?? log.id ?? idx} log={log} />
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableWrapper>

            {/* Pagination */}
            {hasSearched && !loading && totalPages > 1 && (
                <PaginationContainer>
                    {/* Left: rows per page */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 14, color: '#777' }}>Rows per page:</span>
                        <RowsSelect value={perPage} onChange={handlePerPageChange}>
                            {[10, 25, 50, 100].map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </RowsSelect>
                    </div>

                    {/* Center: page controls */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            flexGrow: 1,
                            justifyContent: 'center',
                        }}
                    >
                        <PageButton disabled={page <= 1} onClick={() => handlePageChange(page - 1)}>
                            ←
                        </PageButton>

                        <PageSelect value={page} onChange={(e) => handlePageChange(Number(e.target.value))}>
                            {Array.from({ length: totalPages }, (_, i) => (
                                <option key={i + 1} value={i + 1}>
                                    {i + 1}
                                </option>
                            ))}
                        </PageSelect>

                        <PageButton disabled={page >= totalPages} onClick={() => handlePageChange(page + 1)}>
                            →
                        </PageButton>
                    </div>

                    {/* Right: total count */}
                    <div style={{ fontSize: 13, color: '#777' }}>
                        {totalCount.toLocaleString()} results
                    </div>
                </PaginationContainer>
            )}
        </Container>
    );
};

export default UserActivityLogs;